import { addMonths } from "./format";

/**
 * Pure time-range logic behind the page range picker (RangePicker.tsx keeps
 * the hooks + UI). Presets resolve against the page's [dataStart, dataEnd];
 * `rangeStateFor` maps an injected from/to window (drill links, legacy deep
 * links) back onto the standard chip it's equivalent to, so a link that means
 * "this month" selects MTD rather than parking the picker on Custom.
 */

export interface RangePreset {
  value: string;
  label: string;
  months: number; // Infinity for "All"; 0 for month-to-date
  /** Window starts at Jan 1 of dataEnd's year (YTD) instead of counting months. */
  startOfYear?: boolean;
  /** Chip hidden on phones (the preset still resolves if persisted) — the full
   * set shows on ≥sm, the short MTD/1M/3M/1Y/5Y/All row on mobile. */
  mobileHidden?: boolean;
}

/** The one preset set every page shares, so the control reads the same
 * everywhere and a selection carries across navigation. The full set shows on
 * desktop; `mobileHidden` chips drop out on phones so the row still fits. */
export const PAGE_RANGE_PRESETS: RangePreset[] = [
  { value: "mtd", label: "MTD", months: 0 },
  { value: "1m", label: "1M", months: 1 },
  { value: "3m", label: "3M", months: 3 },
  { value: "6m", label: "6M", months: 6, mobileHidden: true },
  { value: "ytd", label: "YTD", months: 0, startOfYear: true, mobileHidden: true },
  { value: "1y", label: "1Y", months: 12 },
  { value: "3y", label: "3Y", months: 36, mobileHidden: true },
  { value: "5y", label: "5Y", months: 60 },
  { value: "10y", label: "10Y", months: 120, mobileHidden: true },
  { value: "all", label: "All", months: Infinity },
];

export interface RangeState {
  kind: "preset" | "custom";
  preset: string;
  from: string; // YYYY-MM-DD (custom only; legacy YYYY-MM tolerated)
  to: string; // YYYY-MM-DD (custom only; legacy YYYY-MM tolerated)
}

/** Short human label for the selected range — "past 1Y" / "all time" /
 * "over range" — used beside deltas so every page words it the same way. */
export function rangeLabel(state: RangeState): string {
  if (state.kind === "custom") return "over range";
  if (state.preset === "all") return "all time";
  if (state.preset === "mtd" || state.preset === "ytd") return state.preset.toUpperCase();
  return `past ${state.preset.toUpperCase()}`;
}

/** Coerce a YYYY-MM month key to a full date bound; full dates pass through.
 * `hi` picks the month-end sentinel ("-31" sorts ≥ any real day) vs start. */
export function asDate(key: string, hi: boolean): string {
  return key.length >= 10 ? key : `${key}${hi ? "-31" : "-01"}`;
}

/** The real date presets count back from: dataEnd itself when it carries a
 * day; for a bare month key, today (when dataEnd is the current month) or
 * that month's end. */
export function anchorDate(dataEnd: string): string {
  if (dataEnd.length >= 10) return dataEnd;
  const now = new Date();
  const nowMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  if (dataEnd === nowMonth) {
    return `${nowMonth}-${String(now.getDate()).padStart(2, "0")}`;
  }
  const [y, m] = dataEnd.split("-").map(Number);
  return `${dataEnd}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
}

/** Same day-of-month, n months earlier (clamped to the target month's end so
 * e.g. Mar 31 − 1M lands on Feb 28/29 rather than overflowing). */
export function monthsBefore(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const ym = addMonths(`${y}-${String(m).padStart(2, "0")}`, -n);
  const [ty, tm] = ym.split("-").map(Number);
  const lastDay = new Date(ty, tm, 0).getDate();
  return `${ym}-${String(Math.min(d, lastDay)).padStart(2, "0")}`;
}

/** A preset's resolved start date against the anchor (unclamped). */
function presetStart(p: RangePreset, anchor: string, lo: string): string {
  if (p.months === Infinity) return lo;
  if (p.startOfYear) return `${anchor.slice(0, 4)}-01-01`;
  if (p.months === 0) return `${anchor.slice(0, 7)}-01`;
  return monthsBefore(anchor, p.months);
}

/**
 * Resolve a range state to inclusive full-date bounds [start, end]
 * (YYYY-MM-DD), so every consumer can compare `date >= start && date <= end`
 * directly — whether the data is date-keyed balances or month-keyed
 * transactions.
 */
export function resolveRange(
  state: RangeState,
  presets: RangePreset[],
  defaultPreset: string,
  dataStart: string,
  dataEnd: string,
): [string, string] {
  const lo = asDate(dataStart, false);
  const hi = asDate(dataEnd, true);
  if (state.kind === "custom") {
    // from/to come from the day picker (YYYY-MM-DD); tolerate legacy month
    // values from before day precision.
    const from = asDate(state.from, false) < lo ? lo : asDate(state.from, false);
    const to = asDate(state.to, true) > hi ? hi : asDate(state.to, true);
    return from <= to ? [from, to] : [to, from];
  }
  // A persisted preset that no longer exists (from an older preset set)
  // falls back to the page default, not blindly to the first chip.
  const p =
    presets.find((r) => r.value === state.preset) ??
    presets.find((r) => r.value === defaultPreset) ??
    presets[0];
  if (p.months === Infinity) return [lo, hi];
  const start = presetStart(p, anchorDate(dataEnd), lo);
  return [start < lo ? lo : start, hi];
}

/**
 * The range state an injected [from, to] window should select: the standard
 * preset it's equivalent to, else Custom. A drill link that means "July" is
 * MTD when today is in July — its end date covers everything through now
 * (points in the future don't exist), and its start is exactly the preset's
 * start. Presets are checked in display order, so in January (when YTD and
 * MTD coincide) the earlier chip wins.
 */
export function rangeStateFor(from: string, to: string, dataStart: string, dataEnd: string): RangeState {
  const custom: RangeState = { kind: "custom", preset: "all", from, to };
  if (!from || !to) return custom;
  const lo = asDate(dataStart, false);
  const hi = asDate(dataEnd, true);
  const anchor = anchorDate(dataEnd);
  const f = asDate(from, false);
  const t = asDate(to, true);
  // Every preset's window runs through "now"; an end short of the anchor is a
  // genuinely historical slice and stays Custom.
  if (t < anchor && t < hi) return custom;
  for (const p of PAGE_RANGE_PRESETS) {
    const start = presetStart(p, anchor, lo);
    const matches = p.months === Infinity ? f <= lo : f === (start < lo ? lo : start) || f === start;
    if (matches) return { kind: "preset", preset: p.value, from, to };
  }
  return custom;
}
